import type { CorrectionMark, WorkIdentityView } from "./work.model.ts";
import { WORK_CORRECTION_COPY } from "./work.constants.ts";
import { Tag } from "../lib/Tag.tsx";

/**
 * What the registry holds about the work itself.
 *
 * The badge counts who has put their name to the work's chain of title. It is
 * deliberately not rendered in the same shapes as the evidence pills below it,
 * which are a submitter describing their own paperwork rather than a headcount
 * of the people willing to stand behind it.
 *
 * Takes `WorkIdentityView` rather than `WorkIdentityModel` — the smaller shape,
 * which is all this renders. A viewing room builds one and cannot supply an
 * owner or a location, so it reuses this component instead of forking it.
 *
 * A CORRECTED VALUE IS NEVER PRINTED AS PLAIN FACT — OND-41. When the model
 * hands a row (or the title) a `corrected` mark, the value is drawn with the
 * word CORRECTED beside it and a sentence saying what the record first said,
 * who corrected it and when. The sentence is `<details>` rather than a tooltip
 * so it is reachable by keyboard and by a screen reader, and so it is in the
 * DOM for a test to find: "shows the error for ever" is a claim about what is
 * on the page.
 */
export function WorkIdentity({ identity }: { identity: WorkIdentityView }) {
  return (
    <section className="work__identity">
      <h2 className="work__title">
        {identity.title}
        {identity.titleCorrected && <Corrected mark={identity.titleCorrected} />}
      </h2>
      <p className="work__attribution">{identity.attribution}</p>

      {identity.badge && (
        <Tag tone={identity.badge.kind}>
          {identity.badge.label}
        </Tag>
      )}

      <dl className="work__facts">
        {identity.rows.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>
              {row.value}
              {row.corrected && <Corrected mark={row.corrected} />}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Corrected({ mark }: { mark: CorrectionMark }) {
  const detail =
    mark.times > 1
      ? WORK_CORRECTION_COPY.markTimes.replace("{times}", String(mark.times)).replace(
          "{from}",
          mark.from,
        )
      : (mark.from === "—"
          ? WORK_CORRECTION_COPY.markDetailEmpty
          : WORK_CORRECTION_COPY.markDetail.replace("{from}", mark.from)
        )
          .replace("{by}", mark.by)
          .replace("{when}", mark.when);

  return (
    <details className="work__corrected">
      <summary className="work__corrected-mark">{WORK_CORRECTION_COPY.mark}</summary>
      <p className="work__corrected-detail">{detail}</p>
    </details>
  );
}
